import React, { useEffect, useMemo } from 'react';
import { BehaviorSubject, Observable } from 'rxjs';
import { Environments } from 'src/environments/environment';
import { log } from 'src/services/LogService';
import { axiosClient } from 'src/providers/session/axiosClient.ts';

import { EnvironmentContext } from './EnvironmentContext';
import { environmentService } from './EnvironmentService';
import { IEnvironment, UiPropertiesRequestDTO } from './types';
import Config from 'src/config.json';

export const EnvironmentProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const className = useMemo(() => `EnvironmentProvider-${Date.now()}`, []);

  const environmentSubject = useMemo(
    () => new BehaviorSubject<IEnvironment>({ loaded: false } as IEnvironment),
    [],
  );

  const appEnv = Config.REACT_APP_ENV || 'default';
  const endpoint = `${Config.REACT_APP_PROTOCOL}://${Config.REACT_APP_BACKEND_URI}:${Config.REACT_APP_BACKEND_PORT}/edge/rest`;

  const baseEnvironment = useMemo(() => {
    const env = Environments.find((e) => e.name === appEnv);

    if (!env) {
      log.error(className, 'ERR001', 'Failed to find environment for', appEnv);
      return null;
    }

    return {
      ...env,
      apiBase: endpoint,
    } as IEnvironment;
  }, [appEnv, endpoint, className]);

  useEffect(() => {
    if (!baseEnvironment) return;

    const request: UiPropertiesRequestDTO = {
      hostname: window.location.hostname,
      port: window.location.port,
      scheme: window.location.protocol,
    };

    axiosClient
      .post<IEnvironment>(`${endpoint}/ui-props`, request)
      .then(({ data }) => {
        const mergedEnvironment: IEnvironment = {
          ...baseEnvironment,
          ...data,
          loaded: true,
        };

        mergedEnvironment.homePage = 'loading/';
        environmentSubject.next(mergedEnvironment);
        environmentService.setEnvironment(mergedEnvironment);

        document.title = mergedEnvironment.titlePrefix || '';
        const meta = document.querySelector("meta[name='description']");
        if (meta) {
          meta.setAttribute('content', 'VisionX');
        }
      })
      .catch((error) => {
        log.error(className, 'GET /ui-props failed', error);
      });
  }, [baseEnvironment, endpoint, environmentSubject, className]);

  const watchEnvironment = (): Observable<IEnvironment> => environmentSubject.asObservable();

  return (
    <EnvironmentContext.Provider value={{ watchEnvironment }}>
      {children}
    </EnvironmentContext.Provider>
  );
};
